/**
 * Builds what the Daily log shows from the family's data: the doses that were
 * planned (packages/core), what the phone logged (dose_logs), BP readings,
 * app events and phones. Pure functions, no screens.
 */
import {
  addDays, afterLogStart, bpChecksOn, daysLeft, logStartOf, dosesForDay, doseState, formatDateISO, formatTime12, LOW_STOCK_DAYS, parseDateISO,
  type InjectionReminder, type Medicine, type Person, type PlannedDose,
} from '@bhorosha/core';
import type { AppDevice, AppEvent, BpReading, DoseLog, MonitorSnapshot } from '@bhorosha/data';

export type Status = 'taken' | 'skipped' | 'missed' | 'due' | 'later';
export interface PersonData { person: Person; meds: Medicine[]; reminders: InjectionReminder[] }

export interface DoseEntry {
  kind: 'dose';
  key: string;
  person: Person;
  time: string;          // planned "HH:MM"
  dose: PlannedDose;
  status: Status;
  log: DoseLog | null;
  /** Marked on the website's Reminder page (no phone log). */
  fromReminderPage?: boolean;
  lateMinutes: number | null;
}
export interface BpEntry { kind: 'bp'; key: string; person: Person; time: string; reading: BpReading }
export interface BpCheckEntry { kind: 'bpcheck'; key: string; person: Person; time: string; status: 'not_done' | 'later' }
export interface EventEntry { kind: 'event'; key: string; person: Person | null; time: string; event: AppEvent }
export type Entry = DoseEntry | BpEntry | BpCheckEntry | EventEntry;

const hhmm = (iso: string) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const localDay = (iso: string) => { const d = new Date(iso); return formatDateISO(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))); };
const minutesOf = (t: string) => { const [h, m] = t.split(':').map(Number); return (h || 0) * 60 + (m || 0); };

/** Status of one planned dose. */
export function statusOf(dose: PlannedDose, log: DoseLog | undefined, rem: InjectionReminder | undefined, now: Date): { status: Status; fromReminderPage: boolean } {
  if (log) return { status: log.status, fromReminderPage: false };
  if (dose.source === 'injection' && rem) {
    const o = (rem.status_overrides as Record<string, string> | undefined)?.[dose.date];
    if (o === 'completed') return { status: 'taken', fromReminderPage: true };
    if (o === 'missed' || o === 'skipped') return { status: o, fromReminderPage: true };
  }
  return { status: doseState(dose, null, now), fromReminderPage: false };
}

/** Everything that happened (or was planned) for one person on one day, in time order. */
export function dayEntries(pd: PersonData, dateISO: string, snap: MonitorSnapshot, now = new Date(), userCanSee?: (userId: string, personId: string) => boolean): Entry[] {
  const p = pd.person;
  const logs = new Map(snap.doses.filter(d => d.patientId === p.id && d.doseDate === dateISO).map(d => [d.id, d]));
  const out: Entry[] = [];
  const planned = dosesForDay(p, pd.meds, pd.reminders, dateISO);
  for (const dose of planned) {
    const log = logs.get(dose.key);
    logs.delete(dose.key);
    const rem = dose.source === 'injection' ? pd.reminders.find(r => r.id === dose.sourceId) : undefined;
    const { status, fromReminderPage } = statusOf(dose, log, rem, now);
    const late = log && log.status === 'taken' && log.takenAt && localDay(log.takenAt) === dateISO ? minutesOf(hhmm(log.takenAt)) - minutesOf(dose.time) : null;
    out.push({ kind: 'dose', key: dose.key, person: p, time: dose.time, dose, status, log: log || null, fromReminderPage, lateMinutes: late });
  }
  // Logged doses that aren't planned any more (medicine changed or removed, or before the log start).
  const start = logStartOf(p);
  for (const log of logs.values()) {
    if (log.slot === 'dialysis') continue; // given at dialysis: shown in its own group, not counted
    const before = !afterLogStart(start, log.doseDate, log.plannedTime || '00:00');
    const dose: PlannedDose = {
      key: log.id, source: log.source, sourceId: log.sourceId, medicineId: log.medicineId, name: log.name || 'Medicine', strength: '', type: '',
      slot: log.slot as PlannedDose['slot'], date: log.doseDate, time: log.plannedTime || '00:00', amount: log.amount, amountLabel: `× ${log.amount}`, label: before ? 'Before the log start' : 'No longer planned',
    };
    out.push({ kind: 'dose', key: log.id, person: p, time: dose.time, dose, status: log.status, log, lateMinutes: null });
  }
  const readings = snap.bp.filter(b => b.patientId === p.id && localDay(b.measuredAt) === dateISO);
  readings.forEach(r => out.push({ kind: 'bp', key: 'bp:' + r.id, person: p, time: hhmm(r.measuredAt), reading: r }));
  // Planned BP checks: done if a reading is within 3 hours.
  for (const t of bpChecksOn(p, dateISO)) {
    const done = readings.some(r => Math.abs(minutesOf(hhmm(r.measuredAt)) - minutesOf(t)) <= 180);
    if (done) continue;
    const passed = new Date(`${dateISO}T${t}:00`) <= now;
    out.push({ kind: 'bpcheck', key: `bpc:${p.id}:${t}`, person: p, time: t, status: passed ? 'not_done' : 'later' });
  }
  snap.events.filter(e => localDay(e.at) === dateISO && (e.patientId === p.id || (!e.patientId && e.userId && userCanSee?.(e.userId, p.id))))
    .forEach(e => out.push({ kind: 'event', key: 'ev:' + e.id + ':' + p.id, person: e.patientId ? p : null, time: hhmm(e.at), event: e }));
  return out.sort((a, b) => a.time.localeCompare(b.time) || order(a) - order(b));
}
const order = (e: Entry) => (e.kind === 'dose' ? 0 : e.kind === 'bpcheck' ? 1 : e.kind === 'bp' ? 2 : 3);

export interface DaySummary { planned: number; due: number; taken: number; skipped: number; missed: number; notMarked: number; onTime: number; bpDone: number; bpPlanned: number }
export function summarize(entries: Entry[], person: Person, dateISO: string): DaySummary {
  const doses = entries.filter((e): e is DoseEntry => e.kind === 'dose');
  const s = { planned: doses.length, due: 0, taken: 0, skipped: 0, missed: 0, notMarked: 0, onTime: 0, bpDone: 0, bpPlanned: bpChecksOn(person, dateISO).length };
  for (const d of doses) {
    if (d.status !== 'later') s.due++;
    if (d.status === 'taken') { s.taken++; if (d.lateMinutes === null || Math.abs(d.lateMinutes) <= 60) s.onTime++; }
    if (d.status === 'skipped') s.skipped++;
    if (d.status === 'missed') s.missed++;
    if (d.status === 'due') s.notMarked++;
  }
  s.bpDone = entries.filter(e => e.kind === 'bp').length;
  return s;
}

/** One row per dose time across the dates, each cell = the combined status of the doses then. */
export type CellStatus = Status | 'none' | 'partial';
export function gridFor(pd: PersonData, dates: string[], snap: MonitorSnapshot, now = new Date()) {
  const rows = new Map<string, { time: string; label: string; cells: Map<string, CellStatus> }>();
  const perMed = new Map<string, { name: string; planned: number; taken: number }>();
  for (const date of dates) {
    const doses = dayEntries(pd, date, snap, now).filter((e): e is DoseEntry => e.kind === 'dose');
    const byTime = new Map<string, DoseEntry[]>();
    doses.forEach(d => { byTime.set(d.time, [...(byTime.get(d.time) || []), d]); });
    for (const [time, list] of byTime) {
      const k = time;
      if (!rows.has(k)) rows.set(k, { time, label: list[0].dose.label, cells: new Map() });
      const st = new Set(list.map(d => d.status));
      let cell: CellStatus;
      if (st.size === 1) cell = [...st][0];
      else if (st.has('missed') || st.has('due')) cell = st.has('taken') ? 'partial' : (st.has('missed') ? 'missed' : 'due');
      else if (st.has('later')) cell = 'later';
      else cell = 'partial';
      rows.get(k)!.cells.set(date, cell);
      for (const d of list) {
        if (d.status === 'later') continue;
        const m = perMed.get(d.dose.name) || { name: d.dose.name, planned: 0, taken: 0 };
        m.planned++; if (d.status === 'taken') m.taken++;
        perMed.set(d.dose.name, m);
      }
    }
  }
  return { rows: [...rows.values()].sort((a, b) => a.time.localeCompare(b.time)), perMed: [...perMed.values()].sort((a, b) => a.name.localeCompare(b.name)) };
}

export function datesBetween(fromISO: string, toISO: string): string[] {
  const out: string[] = [];
  for (let d = parseDateISO(fromISO)!; formatDateISO(d) <= toISO; d = addDays(d, 1)) out.push(formatDateISO(d));
  return out;
}

export function stockList(pd: PersonData) {
  return pd.meds.map(m => {
    const left = daysLeft(m, pd.person);
    return { id: m.id, name: m.name, onHand: Number(m.on_hand) || 0, daysLeft: left, low: left !== null && left <= LOW_STOCK_DAYS };
  });
}

// ---------------- phones ----------------
export const STALE_HOURS = 24;
export interface PhoneHealth { device: AppDevice; problems: string[]; stale: boolean }
export function phoneHealth(d: AppDevice, now = new Date()): PhoneHealth {
  const problems: string[] = [];
  const last = d.lastSyncAt || d.lastSeenAt;
  const stale = !last || now.getTime() - new Date(last).getTime() > STALE_HOURS * 3600000;
  if (d.notifications === 'blocked') problems.push('Alerts blocked on the phone');
  if (d.exactAlarms === false) problems.push('Exact alarms off (reminders may be late)');
  if (d.batteryOptimised === true) problems.push('Battery saver may stop reminders');
  if (stale) problems.push(last ? `No sync for ${Math.floor((now.getTime() - new Date(last).getTime()) / 3600000)} hours` : 'Never synced');
  return { device: d, problems, stale };
}

// ---------------- text ----------------
export const STATUS_TEXT: Record<Status, string> = { taken: 'Taken', skipped: 'Skipped', missed: 'Missed', due: 'Not marked yet', later: 'Later' };
export const FROM_TEXT: Record<string, string> = { mobile: 'on the phone', notification: 'from the reminder', web: 'on the website' };
export const EVENT_TEXT: Record<string, string> = {
  signed_in: 'Signed in on the phone', signed_out: 'Signed out of the phone', app_opened: 'Opened the app',
  reminder_shown: 'Reminder rang', reminder_opened: 'Opened the reminder', reminder_action: 'Used the reminder buttons',
  reminder_snoozed: 'Reminder put off', dose_undone: 'Undo', stock_low: 'Running low', sync_ok: 'Synced', sync_failed: 'Could not sync',
  alerts_blocked: 'Alerts were turned off on the phone', exact_alarm_blocked: 'Exact alarms are off on the phone',
  battery_saver_on: 'Battery saver is on for Bhorosha', time_changed: 'Phone time or time zone changed', bp_alert: 'Carer alerted about blood pressure',
};
export function eventText(e: AppEvent): string {
  const d = e.data || {};
  if (e.kind === 'dose_undone') return `Undo: ${d.name || 'dose'} at ${formatTime12(String(d.planned_time || ''))} (was ${d.was || '—'})`;
  if (e.kind === 'reminder_opened' || e.kind === 'reminder_shown' || e.kind === 'reminder_snoozed') return `${EVENT_TEXT[e.kind]}${d.time ? ` (${formatTime12(String(d.time))})` : ''}`;
  if (e.kind === 'reminder_action') return `${String(d.action || '') === 'taken_all' ? 'Taken all' : 'Skip'} from the reminder (${formatTime12(String(d.time || ''))})`;
  if (e.kind === 'stock_low') return `Running low: ${d.name || ''}${d.days !== undefined ? `, about ${d.days} days left` : ''}`;
  return EVENT_TEXT[e.kind] || e.kind;
}

export function csvEscape(v: unknown) {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
